import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveBackendAssetUrl } from "../../lib/api";
import {
  UtensilsCrossed, MapPin, Bell,
  ChevronRight, Sun, Coffee, Moon, Utensils,
  CalendarDays, HandHeart, Camera, MessageCircle,
} from "lucide-react";
import { CELIA_BOT_URL } from "../../lib/celia";

// Images (defaults; can be overridden per slot from admin -> /admin/media)
const DEFAULT_IMG = {
  hero: "https://static.prod-images.emergentagent.com/jobs/c8ad0ce5-0940-4e2d-ad44-0bdd429a2d27/images/23a1ceb1bd5c5f89508acc04fa3343026a7c3c8d19debafcfe945d30c0937a90.png",
  mimosa: "https://static.prod-images.emergentagent.com/jobs/c8ad0ce5-0940-4e2d-ad44-0bdd429a2d27/images/c1529cd7b28c0fdb065b2e7ac7f5a7055572a8986fb48673a5ffb217f255c7d5.png",
  aperitivo: "https://static.prod-images.emergentagent.com/jobs/c8ad0ce5-0940-4e2d-ad44-0bdd429a2d27/images/9c9204458e78f174b73ab4d6ae84a5d5682bf8c1dcdec8e37cdee1e7a3d8b325.png",
  noche: "https://static.prod-images.emergentagent.com/jobs/c8ad0ce5-0940-4e2d-ad44-0bdd429a2d27/images/71410eabbdd104930eb63f7d071d0571dea6b85c2fa46f6cba34b0663be7b840.png",
  comida: "https://images.unsplash.com/photo-1528605248644-14dd04022da1?auto=format&fit=crop&w=1200&q=70",
  tardeo: "https://images.unsplash.com/photo-1559329007-40df8a9345d8?auto=format&fit=crop&w=1200&q=70",
};

const DEFAULT_HERO_TITLE = "La Casa del Pueblo vuelve a ser tu casa";
const LEGACY_HERO_CTA = "Descubre La Mimosa de la Feria";
const CELIA_AVATAR = "/images/celia/Celia_perfil.jpg";

const QUICK = [
  { to: "/carta", label: "Carta y precios", icon: UtensilsCrossed, desc: "Raciones, montados, vinos y Mimosa", testid: "quick-carta" },
  { to: "/ubicacion", label: "Ubicación", icon: MapPin, desc: "Consulta cómo llegar", testid: "quick-ubicacion" },
  { to: "/galeria", label: "📸 Sube tu foto", icon: Camera, desc: "Puede aparecer en nuestras pantallas", testid: "quick-galeria" },
  { href: CELIA_BOT_URL, label: "Pregunta a Celia", icon: MessageCircle, desc: "Carta, programa 2026, ubicación e historia", testid: "quick-celia", avatar: CELIA_AVATAR },
];

const EXPERIENCE_DEFS = [
  { key: "aperitivo", label: "Aperitivo", icon: Coffee, caption: "Empieza la Feria con buen sabor y amigos." },
  { key: "comida", label: "Comida", icon: Utensils, caption: "Comida casera, raciones y platos para compartir." },
  { key: "tardeo", label: "Tardeo", icon: Sun, caption: "La Mimosa, música y el mejor ambiente de la Feria." },
  { key: "noche", label: "Noche", icon: Moon, caption: "Copas, encuentros y buena vibra hasta que amanezca." },
];

export default function Home() {
  const [home, setHome] = useState(null);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [media, setMedia] = useState({});

  const loadMedia = () => {
    api.get("/content/media", { params: { _t: Date.now() } })
      .then((r) => setMedia(r.data.data || {}))
      .catch(() => {});
  };

  useEffect(() => {
    api.get("/content/home").then((r) => setHome(r.data.data || {})).catch(() => setHome({}));
    api.get("/alerts/active").then((r) => setActiveAlerts(r.data || [])).catch(() => {});
    loadMedia();
    const onVisible = () => {
      if (document.visibilityState === "visible") loadMedia();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", loadMedia);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", loadMedia);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!home) return <div className="p-8 text-muted-foreground">Cargando...</div>;

  const version = media._updated_at;
  const img = (slot) => resolveBackendAssetUrl(media[slot], version) || DEFAULT_IMG[slot];
  const EXPERIENCES = EXPERIENCE_DEFS.map((e) => ({ ...e, img: img(e.key) }));
  const heroTitle = home.hero_title?.trim() || DEFAULT_HERO_TITLE;
  const heroSubtitle = home.hero_subtitle?.trim() || "Tu punto de encuentro en la Feria de Albacete.";
  const cmsHeroCta = home.hero_cta?.trim();
  const heroCta = !cmsHeroCta || cmsHeroCta === LEGACY_HERO_CTA
    ? "Ver carta y precios"
    : cmsHeroCta;
  const mimosaTitle = home.mimosa_title || "La Mimosa de la Feria";
  const mimosaKicker = home.mimosa_kicker || "VINO ESPUMOSO DE BODEGAS VELASCO";
  const mimosaBody =
    home.mimosa_body ||
    "Nuestro cóctel estrella: vino espumoso de Bodegas Velasco y zumo de naranja natural. El brindis oficial de Casa del Pueblo.";

  return (
    <div className="space-y-12 animate-in fade-in duration-500 -mx-4 sm:mx-0">
      {activeAlerts[0] && (
        <Link
          to="/avisos"
          className="group mx-4 sm:mx-0 block overflow-hidden rounded-2xl border border-primary/30 bg-white text-foreground shadow-sm transition hover:border-primary/50 hover:shadow-md"
          data-testid="home-alert-banner"
        >
          <div className="flex items-stretch">
            {activeAlerts[0].image_url && (
              <div className="w-28 sm:w-40 shrink-0 bg-accent">
                <img
                  src={resolveBackendAssetUrl(activeAlerts[0].image_url, activeAlerts[0].created_at)}
                  alt={activeAlerts[0].title}
                  className="h-full min-h-[112px] w-full object-cover"
                />
              </div>
            )}
            <div className="min-w-0 flex-1 p-4">
              <div className="flex items-start gap-3">
                <Bell size={20} className="text-primary mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs uppercase tracking-widest text-primary font-bold">Hoy en Casa del Pueblo</div>
                  <div className="font-display font-bold text-base sm:text-lg truncate">{activeAlerts[0].title}</div>
                  <div className="text-sm text-muted-foreground line-clamp-2">{activeAlerts[0].message}</div>
                </div>
                <ChevronRight
                  size={18}
                  className="mt-1 shrink-0 text-primary/70 transition group-hover:translate-x-0.5 group-hover:text-primary"
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>
        </Link>
      )}

      <section className="relative overflow-hidden sm:rounded-3xl bg-neutral-900 text-white min-h-[560px] sm:min-h-[620px]">
        <img
          src={img("hero")}
          alt="Casa del Pueblo en la Feria de Albacete"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black/85" />

        <div className="relative z-[1] px-6 sm:px-10 pt-32 sm:pt-36 pb-10 flex flex-col justify-end min-h-[560px] sm:min-h-[620px]">
          <h1
            className="font-display font-black leading-[0.95] text-balance text-[44px] sm:text-6xl lg:text-7xl"
            data-testid="home-hero-title"
          >
            {heroTitle === DEFAULT_HERO_TITLE ? (
              <>
                La Casa del Pueblo
                <br />
                vuelve a ser{" "}
                <span className="font-script text-primary text-[58px] sm:text-7xl lg:text-8xl font-bold italic inline-block align-baseline leading-none">
                  tu casa
                </span>
              </>
            ) : heroTitle}
          </h1>

          <p className="mt-5 text-lg sm:text-xl text-white/90 max-w-xl text-balance" data-testid="home-hero-subtitle">
            {heroSubtitle}
          </p>

          <div className="mt-5 flex flex-wrap gap-2 sm:gap-3" data-testid="home-hero-pills">
            <Pill icon={MapPin} text="Junto al R2, 9 arcos" />
            <Pill icon={CalendarDays} text="Del 7 al 17 de septiembre" />
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/carta"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 rounded-full font-bold hover:brightness-110 hover:scale-[1.02] active:scale-[0.98] transition-transform shadow-lg"
              data-testid="home-hero-cta-carta"
            >
              {heroCta} <ChevronRight size={18} />
            </Link>
            <Link
              to="/ubicacion"
              className="inline-flex items-center gap-2 bg-white/10 border border-white/40 backdrop-blur-md text-white px-6 py-3.5 rounded-full font-bold hover:bg-white hover:text-foreground transition"
              data-testid="home-hero-cta-ubicacion"
            >
              Cómo llegar <ChevronRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 sm:px-0">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display font-black text-2xl">Accesos rápidos</h2>
          <span className="text-[11px] uppercase tracking-widest text-muted-foreground">QR friendly</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="home-quick-access">
          {QUICK.map((q) => {
            const Icon = q.icon;
            const content = (
              <>
                <div className={`${q.avatar ? "h-10 w-10" : "h-11 w-11"} shrink-0 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-sm group-hover:scale-105 transition overflow-hidden`}>
                  {q.avatar ? (
                    <img
                      src={q.avatar}
                      alt="Celia, asistente virtual de Casa del Pueblo"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Icon size={20} strokeWidth={2.5} />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-black text-base sm:text-lg leading-tight">{q.label}</div>
                  <div className="text-[13px] text-muted-foreground mt-0.5 leading-snug">{q.desc}</div>
                  {q.avatar && <div className="mt-1 text-[11px] font-bold uppercase tracking-wide text-primary/80">Disponible en Telegram</div>}
                </div>
              </>
            );
            if (q.href) {
              return (
                <a
                  key={q.href}
                  href={q.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={q.testid}
                  className="group bg-white rounded-2xl p-4 sm:p-5 border border-primary/20 hover:border-primary/50 hover:shadow-md transition-all flex gap-2.5 items-start"
                >
                  {content}
                </a>
              );
            }
            return (
              <Link
                key={q.to}
                to={q.to}
                data-testid={q.testid}
                className="group bg-white rounded-2xl p-4 sm:p-5 border border-border hover:border-primary/40 hover:shadow-md transition-all flex gap-3 items-start"
              >
                {content}
              </Link>
            );
          })}
        </div>
      </section>

      <section
        className="relative overflow-hidden sm:rounded-3xl bg-primary text-primary-foreground min-h-[360px] sm:min-h-[420px]"
        data-testid="home-mimosa"
      >
        <img
          src={img("mimosa")}
          alt="Brindis con La Mimosa de la Feria"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/70 via-primary/40 to-primary/85" />
        <div className="relative z-[1] p-6 sm:p-10 flex flex-col justify-center min-h-[360px] sm:min-h-[420px] max-w-xl">
          <div className="text-[11px] uppercase tracking-[0.3em] font-bold opacity-90">Producto estrella</div>
          <h2 className="mt-2 leading-[0.95]">
            <span className="block font-script text-5xl sm:text-7xl italic font-bold">{mimosaTitle}</span>
          </h2>
          <div className="mt-4 text-xs sm:text-sm font-black uppercase tracking-[0.2em]">
            {mimosaKicker}
          </div>
          <p className="mt-3 text-base sm:text-lg opacity-95 text-balance">
            {mimosaBody}
          </p>
          <Link
            to="/carta"
            className="mt-6 self-start inline-flex items-center gap-2 bg-white text-primary px-6 py-3 rounded-full font-bold hover:scale-[1.02] active:scale-[0.98] transition"
            data-testid="home-mimosa-cta"
          >
            Ver en carta <ChevronRight size={16} />
          </Link>
        </div>
      </section>

      <section className="px-4 sm:px-0">
        <h2 className="font-display font-black text-2xl mb-4">Vive cada momento</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="home-experiences">
          {EXPERIENCES.map((e) => {
            const Icon = e.icon;
            return (
              <article key={e.key} className="group">
                <div className="relative overflow-hidden rounded-2xl aspect-[4/5] sm:aspect-[3/4]">
                  <img
                    src={e.img}
                    alt={e.label}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute top-3 left-3">
                    <span className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground px-2.5 py-1 rounded-md text-[11px] font-black uppercase tracking-wider shadow-md">
                      <Icon size={12} strokeWidth={3} />
                      {e.label}
                    </span>
                  </div>
                </div>
                <p className="mt-3 text-sm text-foreground/80 leading-snug">{e.caption}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="px-4 sm:px-0" data-testid="home-closing">
        <div className="bg-white rounded-3xl border border-border p-6 sm:p-8">
          <div className="flex items-start gap-3">
            <HandHeart size={24} className="text-primary shrink-0 mt-1" />
            <div>
              <h3 className="font-display font-black text-xl sm:text-2xl">
                {home.intro_title || "Feria interior, con identidad propia"}
              </h3>
              <p className="mt-2 text-muted-foreground text-balance leading-relaxed">
                {home.intro_body}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Pill({ icon: Icon, text }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/30 backdrop-blur-md px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium">
      <Icon size={14} strokeWidth={2.5} />
      {text}
    </span>
  );
}
